import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";
import { FiTarget, FiEye, FiHeart, FiCheckCircle, FiArrowLeft, FiArrowRight, FiX, FiShield, FiCompass } from "react-icons/fi";

// Import translations
import fa from "./locales/OurBeliefs-fa.json";
import en from "./locales/OurBeliefs-en.json";
import ar from "./locales/OurBeliefs-ar.json";
import tr from "./locales/OurBeliefs-tr.json";

// Interface for cards
interface BeliefCard {
    id: string;
    title: string;
    summary: string;
    gradient: string;
    borderColor: string;
    textColor: string;
    image: string;
    fallbackImage: string;
    badges: string[];
    icon?: React.ReactNode;
}

const CARD_DISPLAY_DURATION = 6000;

// Helper to get card icon
const getCardIcon = (id: string) => {
    switch (id) {
        case "mission":
            return <FiTarget className="w-5 h-5 text-[#0072CE]" />;
        case "vision":
            return <FiEye className="w-5 h-5 text-[#0072CE]" />;
        case "values":
            return <FiHeart className="w-5 h-5 text-[#0072CE]" />;
        default:
            return <FiCompass className="w-5 h-5 text-[#0072CE]" />;
    }
};

// Component for clean, non-cluttered badge tags
const BadgeList: React.FC<{ badges: string[] }> = ({ badges }) => {
    return (
        <div className="flex flex-wrap gap-2 mt-3">
            {badges.map((badge, index) => (
                <span
                    key={index}
                    className="px-3 py-1 bg-slate-100/90 text-slate-700 border border-slate-200/80 rounded-full text-xs font-semibold"
                >
                    {badge}
                </span>
            ))}
        </div>
    );
};

// Component for Belief Card
const BeliefCardComponent: React.FC<{
    card: BeliefCard;
    isActive: boolean;
    onClick: () => void;
    onOpenModal: (card: BeliefCard) => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    index: number;
    isRTL: boolean;
    textAlign: string;
}> = ({ card, isActive, onClick, onOpenModal, onMouseEnter, onMouseLeave, index, isRTL, textAlign }) => {
    return (
        <motion.div
            key={card.id}
            className={`p-5 sm:p-6 rounded-2xl cursor-pointer transition-all relative border ${
                isActive
                    ? "bg-white shadow-xl shadow-blue-500/10 border-blue-400 ring-2 ring-blue-500/10"
                    : "bg-white/80 backdrop-blur-sm border-slate-200/80 shadow-xs hover:border-blue-200 hover:shadow-md"
            } overflow-hidden`}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.99 }}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: index * 0.08 }}
        >
            <div className="flex flex-col relative z-10">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${isActive ? "bg-blue-100 text-[#0072CE]" : "bg-slate-100 text-slate-600"}`}>
                            {getCardIcon(card.id)}
                        </div>
                        <h3 className={`text-lg sm:text-xl font-bold ${isActive ? "text-[#0038A8]" : "text-slate-800"} ${textAlign}`}>
                            {card.title}
                        </h3>
                    </div>

                    {/* Minimal active indicator dot */}
                    {isActive && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-[#0072CE] text-[11px] font-bold">
                            <span className="w-2 h-2 rounded-full bg-[#0072CE] animate-pulse"></span>
                            <span>{isRTL ? "فعال" : "Active"}</span>
                        </div>
                    )}
                </div>

                <p className={`text-slate-600 leading-relaxed text-xs sm:text-sm mb-3 ${textAlign} line-clamp-3`}>
                    {card.summary}
                </p>

                {/* Show badges on active or hover */}
                {card.badges && card.badges.length > 0 && <BadgeList badges={card.badges} />}

                {/* Footer action to open detailed modal */}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenModal(card);
                        }}
                        className="text-xs font-bold text-[#0072CE] hover:text-[#0038A8] flex items-center gap-1.5 transition-colors"
                    >
                        <span>{isRTL ? "مطالعه کامل و جزئیات" : "Read Full Details"}</span>
                        {isRTL ? <FiArrowLeft className="w-3.5 h-3.5" /> : <FiArrowRight className="w-3.5 h-3.5" />}
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

// Component for Image display
const ImageSection: React.FC<{
    activeCard: BeliefCard;
    beliefCards: BeliefCard[];
    setActiveTab: (id: string) => void;
    onOpenModal: (card: BeliefCard) => void;
    isRTL: boolean;
}> = ({ activeCard, beliefCards, setActiveTab, onOpenModal, isRTL }) => {
    return (
        <motion.div
            className="hidden lg:flex lg:w-1/2 order-2 lg:order-1 self-stretch flex-col justify-stretch flex-1"
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="relative w-full h-full flex-1 flex flex-col self-stretch">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeCard.id}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.4 }}
                        className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-200/80 group cursor-pointer w-full h-full flex-1 flex flex-col justify-end min-h-[500px]"
                        onClick={() => onOpenModal(activeCard)}
                    >
                        <img
                            src={activeCard.image}
                            alt={activeCard.title}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = activeCard.fallbackImage;
                            }}
                        />

                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent"></div>

                        {/* Active Title Banner */}
                        <div className={`relative z-10 p-6 ${isRTL ? "text-right" : "text-left"}`}>
                            <div className="bg-white/95 backdrop-blur-md p-4 sm:p-5 rounded-2xl shadow-2xl border border-white/50 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3.5">
                                    <div className="w-11 h-11 rounded-xl bg-blue-100 text-[#0072CE] flex items-center justify-center shrink-0 shadow-xs">
                                        {getCardIcon(activeCard.id)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-[#0072CE] animate-pulse" />
                                            <h4 className="text-sm font-bold text-slate-900">{activeCard.title}</h4>
                                        </div>
                                        <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{activeCard.summary}</p>
                                    </div>
                                </div>
                                <span className="shrink-0 text-xs font-bold text-[#0072CE] bg-blue-50 hover:bg-blue-100/80 px-3.5 py-1.5 rounded-full border border-blue-100 transition-colors">
                                    {isRTL ? "نمایش کامل" : "View"}
                                </span>
                            </div>
                        </div>

                        {/* Tab Navigation Dots */}
                        <div className={`absolute top-6 ${isRTL ? "right-6" : "left-6"} z-10 flex gap-2 bg-slate-900/60 backdrop-blur-md px-3.5 py-2 rounded-full border border-white/20`}>
                            {beliefCards.map((card) => (
                                <button
                                    key={card.id}
                                    type="button"
                                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                                        activeCard.id === card.id ? "bg-white scale-125" : "bg-white/40 hover:bg-white/70"
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveTab(card.id);
                                    }}
                                />
                            ))}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

// Main Component
const OurBeliefs: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "OurBeliefs";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [activeTab, setActiveTab] = useState<string>("mission");
    const [isAutoPlay, setIsAutoPlay] = useState<boolean>(true);
    const [selectedModalCard, setSelectedModalCard] = useState<BeliefCard | null>(null);

    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";
    const textAlign = isRTL ? "text-right" : "text-left";

    const beliefCards: BeliefCard[] = useMemo(() => {
        const cards = [];
        for (let i = 0; i < 10; i++) {
            const card = t(`cards.${i}`, { ns: "OurBeliefs", returnObjects: true, defaultValue: null }) as any;
            if (card && card.id) {
                cards.push({
                    ...card,
                    gradient: "",
                    borderColor: "",
                    textColor: "text-slate-800",
                });
            } else {
                break;
            }
        }
        return cards;
    }, [t, i18n.language]);

    const activeCard = useMemo(() => beliefCards.find((card) => card.id === activeTab), [activeTab, beliefCards]);

    // Auto switch cards
    useEffect(() => {
        if (!isAutoPlay || beliefCards.length === 0 || selectedModalCard !== null) return;

        const interval = setInterval(() => {
            const currentIndex = beliefCards.findIndex((card) => card.id === activeTab);
            const nextIndex = (currentIndex + 1) % beliefCards.length;
            setActiveTab(beliefCards[nextIndex].id);
        }, CARD_DISPLAY_DURATION);

        return () => clearInterval(interval);
    }, [activeTab, isAutoPlay, beliefCards, selectedModalCard]);

    const handleMouseEnter = () => setIsAutoPlay(false);
    const handleMouseLeave = () => setIsAutoPlay(true);

    const handleCardClick = (cardId: string) => {
        setActiveTab(cardId);
    };

    const closeModal = () => setSelectedModalCard(null);

    const scrollToContact = () => {
        closeModal();
        const el = document.getElementById("contact");
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
        }
    };

    if (!activeCard || beliefCards.length === 0) return null;

    return (
        <section dir={dir} id="our-beliefs" className="pb-32 relative overflow-hidden">
            <div className="container mx-auto px-4 relative z-10">
                <SectionHeader
                    regularText={t("sectionHeader.regularText", { ns: "OurBeliefs" })}
                    highlightedText={t("sectionHeader.highlightedText", { ns: "OurBeliefs" })}
                    descriptionHighlight={t("sectionHeader.descriptionHighlight", { ns: "OurBeliefs" })}
                    descriptionText={t("sectionHeader.descriptionText", { ns: "OurBeliefs" })}
                    className="pb-6"
                />

                <div className={`flex flex-col lg:flex-row-reverse gap-8 max-w-7xl mx-auto lg:items-stretch items-center`}>
                    {/* Card List */}
                    <div className="w-full lg:w-1/2 flex flex-col gap-4">
                        {beliefCards.map((card, index) => (
                            <BeliefCardComponent
                                key={card.id}
                                card={card}
                                isActive={activeTab === card.id}
                                onClick={() => handleCardClick(card.id)}
                                onOpenModal={(c) => setSelectedModalCard(c)}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                index={index}
                                isRTL={isRTL}
                                textAlign={textAlign}
                            />
                        ))}
                    </div>

                    {/* Image Preview */}
                    <ImageSection
                        activeCard={activeCard}
                        beliefCards={beliefCards}
                        setActiveTab={setActiveTab}
                        onOpenModal={(c) => setSelectedModalCard(c)}
                        isRTL={isRTL}
                    />
                </div>
            </div>

            {/* Redesigned Card Modal */}
            <AnimatePresence>
                {selectedModalCard !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto"
                        onClick={closeModal}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 15 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 15 }}
                            transition={{ type: "spring", duration: 0.4 }}
                            className="relative max-w-xl w-full max-h-[85vh] bg-white p-0 rounded-3xl shadow-2xl border border-slate-100 overflow-y-auto my-auto scrollbar-thin scrollbar-thumb-slate-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Top Gradient Accent Line */}
                            <div className="h-1.5 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] w-full sticky top-0 z-30"></div>

                            {/* Header Section without image */}
                            <div className="p-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-4 bg-gradient-to-b from-blue-50/40 via-white to-white">
                                <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-blue-50 text-[#0072CE] border border-blue-100/80 flex items-center justify-center shrink-0 shadow-2xs">
                                        {getCardIcon(selectedModalCard.id)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-[#0072CE] animate-pulse" />
                                            <h3 className="text-lg sm:text-xl font-bold text-slate-900">{selectedModalCard.title}</h3>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-0.5">{isRTL ? "باور و چشم‌انداز راهبردی" : "Strategic Belief & Vision"}</p>
                                    </div>
                                </div>

                                {/* Close Button */}
                                <button
                                    onClick={closeModal}
                                    className="w-9 h-9 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex items-center justify-center cursor-pointer shrink-0"
                                    aria-label="Close"
                                >
                                    <FiX className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Modal Content Details */}
                            <div className="p-6 sm:p-7 space-y-5">
                                {/* Summary & Core Statement */}
                                <div className="bg-slate-50/90 p-4 sm:p-5 rounded-2xl border border-slate-200/70">
                                    <p className="text-slate-700 text-xs sm:text-sm leading-relaxed font-medium">
                                        {selectedModalCard.summary}
                                    </p>
                                </div>

                                {/* Key Pillars / Badges Breakdown */}
                                {selectedModalCard.badges && selectedModalCard.badges.length > 0 && (
                                    <div>
                                        <h4 className="text-xs sm:text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                                            <FiCheckCircle className="w-4 h-4 text-[#0072CE]" />
                                            <span>{isRTL ? "ارکان و اصول کلیدی" : "Core Principles & Pillars"}</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                            {selectedModalCard.badges.map((badge, bIdx) => (
                                                <div
                                                    key={bIdx}
                                                    className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50/80 border border-slate-100 text-xs text-slate-700 font-semibold"
                                                >
                                                    <FiCheckCircle className="w-4 h-4 text-[#0072CE] shrink-0" />
                                                    <span>{badge}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Strategic Commitment Box */}
                                <div className="p-4 rounded-2xl bg-blue-50/60 border border-blue-100/80 flex items-center gap-3.5">
                                    <div className="w-10 h-10 rounded-xl bg-blue-100 text-[#0072CE] flex items-center justify-center shrink-0">
                                        <FiShield className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h5 className="text-xs font-bold text-slate-900">{isRTL ? "تعهد راهبردی دلبند" : "Delband Strategic Commitment"}</h5>
                                        <p className="text-[11px] text-slate-600 mt-0.5">
                                            {isRTL
                                                ? "توسعه فناوری بومی و استاندارد برای مراقبت هوشمند از سلامت و ایمنی خانواده‌ها"
                                                : "Developing reliable technology standards for smart family health and safety care"}
                                        </p>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 pt-3 border-t border-slate-100">
                                    <motion.button
                                        className="flex-1 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] text-white py-3 px-5 rounded-xl font-semibold text-xs sm:text-sm shadow-md shadow-blue-500/20 hover:shadow-lg transition-all text-center flex items-center justify-center gap-2 cursor-pointer"
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={scrollToContact}
                                    >
                                        <span>{isRTL ? "ارتباط با تیم دلبند" : "Contact Delband Team"}</span>
                                        {isRTL ? <FiArrowLeft className="w-4 h-4" /> : <FiArrowRight className="w-4 h-4" />}
                                    </motion.button>
                                    <button
                                        className="px-5 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs sm:text-sm font-semibold transition-colors cursor-pointer"
                                        onClick={closeModal}
                                    >
                                        {isRTL ? "بستن" : "Close"}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default OurBeliefs;
